import api from '@/lib/api';
import type { PluginPermission } from '@fias/arche-sdk';

/**
 * Cached, single-flight minting of preview bridge capability tokens.
 *
 * The builder preview's PluginBridgeHost authenticates against the live
 * plugins bridge with a 5-minute session-scoped token (ADR-012 Phase M3).
 * A plugin firing many bridge ops in parallel on mount must not fan out into
 * parallel mints, and a token must be re-minted when the plugin's declared
 * permissions change — this module owns both concerns so the page's
 * `getAuthToken` callback stays a one-liner.
 *
 * Cache rules:
 * - Entries expire `REFRESH_MARGIN_MS` before the server TTL (duration-based
 *   math — immune to absolute clock skew), mirroring
 *   `authApi.getPluginBridgeToken`'s margin for published plugins.
 * - The cache key is the sessionId; an entry is only a hit when the caller's
 *   permission fingerprint matches the one the entry was minted for, so a
 *   manifest permission edit re-mints on the very next bridge op.
 * - If the server grants a DIFFERENT permission set than requested (its copy
 *   of the manifest lags the editor in either direction), the token is still
 *   returned but cached with a short TTL so the next op re-mints instead of
 *   pinning the divergence for 5 minutes.
 * - `invalidatePreviewBridgeToken` bumps a generation counter; an in-flight
 *   mint that resolves against a stale generation does NOT repopulate the
 *   cache (session switch / unmount / reset races).
 *
 * On mint failure this THROWS with the server's message. Never swallow into
 * an empty-string token — that was the cryptic "JWT … 3 parts" 401 class of
 * bug the builder preview has history with.
 */

const REFRESH_MARGIN_MS = 30_000;
/** Cache lifetime when the server's granted set diverges from the request. */
const MISMATCH_TTL_MS = 5_000;

interface CacheEntry {
  token: string;
  expiresAt: number;
  fingerprint: string;
  generation: number;
}

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, { promise: Promise<string>; fingerprint: string }>();
const generations = new Map<string, number>();

function currentGeneration(sessionId: string): number {
  return generations.get(sessionId) ?? 0;
}

function fingerprintOf(permissions: readonly string[]): string {
  return [...permissions].sort().join(',');
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return fingerprintOf(a) === fingerprintOf(b);
}

export async function getPreviewBridgeToken(
  sessionId: string,
  permissions: readonly PluginPermission[],
): Promise<string> {
  const fingerprint = fingerprintOf(permissions);

  const hit = cache.get(sessionId);
  if (hit && hit.expiresAt > Date.now() && hit.fingerprint === fingerprint) {
    return hit.token;
  }

  // Single-flight: concurrent callers with the same fingerprint share one
  // mint. A caller with a NEW fingerprint (permissions just changed) must
  // not piggyback on the stale in-flight request.
  const pending = inflight.get(sessionId);
  if (pending && pending.fingerprint === fingerprint) {
    return pending.promise;
  }

  const generation = currentGeneration(sessionId);
  const entry: { promise: Promise<string>; fingerprint: string } = {
    promise: Promise.resolve(''),
    fingerprint,
  };
  entry.promise = (async () => {
    try {
      const {
        token,
        expiresIn,
        permissions: granted,
      } = await api.mintBuilderPreviewBridgeToken(sessionId);
      if (currentGeneration(sessionId) === generation) {
        const grantedMatchesRequest = sameSet(granted, permissions);
        cache.set(sessionId, {
          token,
          expiresAt:
            Date.now() +
            (grantedMatchesRequest
              ? Math.max(0, expiresIn * 1000 - REFRESH_MARGIN_MS)
              : MISMATCH_TTL_MS),
          fingerprint,
          generation,
        });
      }
      return token;
    } finally {
      if (inflight.get(sessionId) === entry) {
        inflight.delete(sessionId);
      }
    }
  })();

  inflight.set(sessionId, entry);
  return entry.promise;
}

/**
 * Drop the cached token (and orphan any in-flight mint) for a session.
 * Callers: session switch / page unmount (PluginBuilderPage) and the
 * bridge's one-shot BRIDGE_TOKEN_EXPIRED recovery (onAuthTokenExpired).
 * Deliberately NOT called around preview-data resets — a reset does not
 * change the session's identity or permissions, and the bridge re-checks
 * session ownership on every op, so the token stays valid and correct.
 */
export function invalidatePreviewBridgeToken(sessionId: string): void {
  generations.set(sessionId, currentGeneration(sessionId) + 1);
  cache.delete(sessionId);
  inflight.delete(sessionId);
}
